import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useRouter } from "next/router";
import { dehydrateApolloClient, initApolloClient } from "@/lib/apolloClient";
import { useAuthState } from "@/contexts/auth/auth.provider";
import { InitialResolver } from "@/lib/use.middlewares";
import { GET_MY_PACKAGES } from "@/graphql/queries/payment";
import { GET_PACKAGES } from "@/graphql/queries/payment";
import { GET_SUB_GENERAL_NOTE, GET_SUB_FAQS } from "@/graphql/queries/info";
import Accordion from "@/components/accordion/accordion";
import { ErrorModal } from "@/components/error/ssr";
import { Spinner } from "@aqar-app/react-ui";
import { Notification } from "@aqar-app/aqar-design-system";
import PricingPackage from "@/components/payment-cards/pricingPackage.jsx";
import Slideshow from "@/components/slideshow";
import styles from "./.module.scss";
import { useState } from "react";

const DiscountIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.icon}
  >
    <path
      d="M13.325 7.18134C13.2663 7.06609 13.2663 6.93391 13.325 6.81868L13.8692 5.75051C14.1721 5.15577 13.9321 4.44689 13.3227 4.13662L12.2281 3.57936C12.11 3.51926 12.0291 3.41229 12.0059 3.28599L11.7919 2.1149C11.6727 1.46287 11.0441 1.02472 10.3611 1.11744L9.13426 1.28396C9.00183 1.3019 8.87089 1.26105 8.77483 1.17188L7.88428 0.345235C7.38841 -0.115065 6.61159 -0.115092 6.11575 0.345235L5.22517 1.17196C5.12908 1.26116 4.99814 1.30193 4.86574 1.28404L3.63888 1.11752C2.95567 1.02475 2.32732 1.46295 2.20812 2.11498L1.99405 3.28602C1.97094 3.41235 1.88998 3.51929 1.77192 3.57942L0.677365 4.13668C0.0679362 4.44691 -0.17212 5.15585 0.130857 5.75059L0.675 6.81874C0.733703 6.93399 0.733703 7.06617 0.675 7.18139L0.130828 8.24954C-0.172148 8.84429 0.0679076 9.55316 0.677337 9.86343L1.77189 10.4207C1.88998 10.4808 1.97094 10.5878 1.99405 10.7141L2.20812 11.8851C2.31663 12.4787 2.84707 12.895 3.4567 12.8949C3.51674 12.8949 3.57767 12.8909 3.63891 12.8826L4.86577 12.7161C4.99808 12.698 5.12911 12.739 5.2252 12.8281L6.11575 13.6548C6.36373 13.885 6.68181 14 7 14C7.31811 14 7.63639 13.8849 7.88425 13.6548L8.77483 12.8281C8.87092 12.739 9.00189 12.6982 9.13426 12.7161L10.3611 12.8826C11.0444 12.9753 11.6727 12.5372 11.7919 11.8851L12.006 10.7141C12.0291 10.5878 12.11 10.4808 12.2281 10.4207L13.3227 9.86343C13.9321 9.55319 14.1721 8.84426 13.8692 8.24951L13.325 7.18134ZM5.31696 3.36647C6.16764 3.36647 6.85974 4.03056 6.85974 4.84681C6.85974 5.66307 6.16764 6.32716 5.31696 6.32716C4.46628 6.32716 3.77418 5.66307 3.77418 4.84681C3.77418 4.03056 4.46628 3.36647 5.31696 3.36647ZM4.52065 9.94994C4.43849 10.0288 4.33081 10.0682 4.22315 10.0682C4.11548 10.0682 4.00777 10.0288 3.92564 9.94994C3.76133 9.79228 3.76133 9.53665 3.92564 9.37899L9.47932 4.05005C9.6436 3.89239 9.91005 3.89239 10.0744 4.05005C10.2387 4.20771 10.2387 4.46335 10.0744 4.62101L4.52065 9.94994ZM8.68298 10.6336C7.8323 10.6336 7.1402 9.96949 7.1402 9.15324C7.1402 8.33698 7.8323 7.67289 8.68298 7.67289C9.53366 7.67289 10.2258 8.33698 10.2258 9.15324C10.2258 9.96949 9.53366 10.6336 8.68298 10.6336Z"
      fill="#4775ED"
    />
    <path
      d="M8.68272 8.4801C8.29605 8.4801 7.98145 8.78195 7.98145 9.15297C7.98145 9.52399 8.29602 9.82584 8.68272 9.82584C9.06939 9.82584 9.38396 9.52399 9.38396 9.15297C9.38396 8.78195 9.06939 8.4801 8.68272 8.4801Z"
      fill="#4775ED"
    />
    <path
      d="M5.31648 4.17346C4.92981 4.17346 4.61523 4.47531 4.61523 4.84633C4.61523 5.21735 4.92981 5.51922 5.31648 5.51922C5.70315 5.51922 6.01775 5.21738 6.01775 4.84633C6.01773 4.47533 5.70315 4.17346 5.31648 4.17346Z"
      fill="#4775ED"
    />
  </svg>
);

function PricingPackages() {
  const { token } = useAuthState();
  const { locale } = useRouter();
  const [activeIndex, setActiveIndex] = useState(0);

  const { data, loading } = useQuery(token ? GET_MY_PACKAGES : GET_PACKAGES, {
    context: { token },
    variables: { new_packages: true },
    onError: ErrorModal.handle,
  });

  const { data: infoData } = useQuery(GET_SUB_GENERAL_NOTE, {
    onError: ErrorModal.handle,
  });

  const { data: faqData } = useQuery(GET_SUB_FAQS, {
    onError: ErrorModal.handle,
  });

  const plans = useMemo(() => {
    return token
      ? data?.Offices?.Package?.getMyPackages
      : data?.Offices?.packages;
  }, [data]);

  const subGeneralNote = useMemo(() => {
    const note = infoData?.Info?.configurations?.sub_general_note;
    if (!note) return "";
    const lang = locale === "en" ? "en" : "ar";
    return note[lang] ?? note.ar ?? note.en ?? "";
  }, [infoData?.Info?.configurations?.sub_general_note, locale]);

  const faqAccordionData = useMemo(() => {
    const faqs = faqData?.Info?.configurations?.sub_faqs;
    if (!Array.isArray(faqs) || faqs.length === 0) return [];
    const lang = locale === "en" ? "en" : "ar";
    return faqs.map((faq) => ({
      header: faq?.question?.[lang] ?? faq?.question?.ar ?? faq?.question?.en ?? "",
      content: faq?.answer?.[lang] ?? faq?.answer?.ar ?? faq?.answer?.en ?? "",
    }));
  }, [faqData?.Info?.configurations?.sub_faqs, locale]);

  if (loading)
    return (
      <div className={styles.Loading}>
        <Spinner />
      </div>
    );

  if (!plans?.length) {
    return (
      <div className={styles.emptyState}>
        <h1 className={styles.title}>
          لا يوجد باقات متاحة حالياً، الرجاء المحاولة لاحقاً
        </h1>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.notification}>
        <DiscountIcon size={20} />
        <div>{subGeneralNote}</div>
      </div>

      {/* Desktop: grid of cards (hidden on mobile) */}
      <div className={styles.cardsGrid}>
        {plans?.map((plan, index) => (
          <div className={styles.card} key={plan.type || index}>
            <PricingPackage plan={plan} isPopular={index === 1} />
            <div className={styles.notesContainer}>
              {plan?.note
                ?.split("\n")
                .map(
                  (currentNote, noteIndex) =>
                    currentNote && (
                      <Notification
                        key={noteIndex}
                        visible
                        position="fixed"
                        type="attention"
                        closable={false}
                        autoDelete={false}
                        message={currentNote}
                        className={styles.notification}
                      />
                    ),
                )}
            </div>
          </div>
        ))}
      </div>

      {/* Mobile: horizontal carousel with bullets (hidden on desktop) */}
      <div className={styles.cardsSlideshow}>
        <Slideshow
          bulletAriaLabel="اختر الباقة"
          onVisibleSlideChange={setActiveIndex}
          slideToShow={1}
        >
          {plans?.map((plan, index) => (
            <div className={styles.card} key={plan.type || index}>
              <PricingPackage plan={plan} isPopular={index === 1} />
            </div>
          ))}
        </Slideshow>
        {plans[activeIndex]?.note
          ?.split("\n")
          .map(
            (currentNote, noteIndex) =>
              currentNote && (
                <Notification
                  key={noteIndex}
                  visible
                  position="fixed"
                  type="attention"
                  closable={false}
                  autoDelete={false}
                  message={currentNote}
                  className={styles.notification}
                />
              ),
          )}
      </div>

      <div className={styles.contactCard}>
        <div className={styles.contactContent}>
          <h2>تحتاج باقة أكبر؟</h2>
          <p>تواصل معنا لبناء باقة مخصصة تلائم كافة احتياجاتك</p>
        </div>
        <a href="mailto:sales@aqar.fm">
          <button className={styles.contactButton}>تواصل معنا</button>
        </a>
      </div>

      {faqAccordionData.length > 0 && (
        <section className={styles.faqSection} aria-labelledby="faq-title">
          <h2 id="faq-title" className={styles.faqTitle}>
            {locale === "en" ? "Frequently Asked Questions" : "الأسئلة الشائعة"}
          </h2>
          <div className={styles.faqAccordionWrapper}>
            <Accordion accordionData={faqAccordionData} />
          </div>
        </section>
      )}
    </div>
  );
}

PricingPackages.getInitialProps = InitialResolver(async (ctx) => {
  const { token } = ctx.authState;
  const apolloClient = initApolloClient();

  await apolloClient.query({
    query: token ? GET_MY_PACKAGES : GET_PACKAGES,
    context: { token },
    variables: { new_packages: true },
  });
  return dehydrateApolloClient(apolloClient, {});
});

export default PricingPackages;
